import React, { useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import FilterBar from './filterBar';
import DigitalEngagementInfo from './digitalEngagementInfo';
import PhysicianEngagement from './physicianEngagement';
import DecileSpecialty from './decileSpecialty';
import DistrictBreakdown from './districtBreakdown';
import SearchUsingFilter from './searchUsingFilter';
import PowerBi from './PowerBi';
import NPITable from './npiTable';
import { useDispatch, useSelector } from 'react-redux';
import { getDigitalEngagement, getProviderPage } from '../../../redux/thunks/engagementThunks';
import { XEVINAPANT_COMPANY_ID } from '../../../constants';
import { actions as digitalEngagementAction } from '../../../redux/reducers/digitalEngagementReducer';
import SuccessfulEvents from './successfulEvents';
import HospitalBreakdown from './hospitalBreakdown';
import { getCompanyConfigurationByTag } from '../../../redux/thunks/companyThunks';

const DigitalPhysicianEngagement = () => {
    const dispatch = useDispatch();
    const { company } = useSelector((state) => state.company);
    const { startDateRange, endDateRange } = useSelector((state) => state.filter);
    const { companyConfig } = useSelector(state => state.adminSettings);
    // Paging + search state drives the request now, so it has to be read here.
    const { currentPage, pageSize, searchTerm } = useSelector(
        (state) => state.digitalEngagement,
    );
    const { value } = companyConfig;
    const hospitalBreakDownCompanies = [190,191,192,233,64,140];

    useEffect(() => {
        if (company.id) {
            dispatch(getCompanyConfigurationByTag({ companyId: company.id }));
        }
    }, [company.id]);

    const isConfigReady = companyConfig && Object.keys(companyConfig).length > 0;

    // lastPageKey  - the page/size/search combination already on screen
    // skipNextTable - set when the dashboard effect runs, so the table effect
    //                 swallows exactly one run. Needed because resetToInitialState
    //                 has not propagated yet when the table effect fires in the
    //                 same commit, so `currentPage` there is still the OLD page.
    const lastPageKey = useRef(null);
    const skipNextTable = useRef(false);

    // The heavy dashboard endpoint runs ONCE per company/date-range. Page turns
    // and search no longer touch it -- they go to the light provider-page
    // endpoint below -- so the graphs and counters are not rebuilt every time
    // somebody clicks "next page".
    //
    // Resetting and fetching in the SAME effect (rather than two effects keyed on
    // overlapping state) is what stops the duplicate request on date change: the
    // reset used to mutate currentPage/searchTerm, which re-triggered the second
    // effect and fired the dashboard call twice.
    useEffect(() => {
        if (company.id || isConfigReady) {
            dispatch(digitalEngagementAction.resetToInitialState());
            // record what the dashboard is fetching (always page 1, no search) so
            // the table effect below does not re-request the same thing
            skipNextTable.current = true;
            lastPageKey.current = [1, pageSize, ''].join('|');
            dispatch(getDigitalEngagement({
                companyId: company.id,
                startRange: startDateRange,
                endRange: endDateRange,
                page: 1,
                pageSize
                //isUnified: value?.toLowerCase() === 'true' || false 
            }));
        }
    }, [isConfigReady, company.id, startDateRange, endDateRange]);

    // Table-only refetch.
    //
    // The dashboard effect above dispatches resetToInitialState, which rewrites
    // currentPage/pageSize/searchTerm. Those are this effect's dependencies, so a
    // date change fired the dashboard request AND this one. Comparing against the
    // last signature actually fetched makes the duplicate impossible: after a
    // dashboard load the signature already records page 1, so this effect sees no
    // change and stays put until the user really does page or search.
    useEffect(() => {
        if (!(company.id || isConfigReady)) return;
        // one run is always swallowed after a company/date change: the dashboard
        // request already covers page 1
        if (skipNextTable.current) {
            skipNextTable.current = false;
            return;
        }
        const pageKey = [currentPage, pageSize, searchTerm || ''].join('|');
        if (lastPageKey.current === pageKey) return;
        lastPageKey.current = pageKey;
        dispatch(getProviderPage({
            companyId: company.id,
            type: 'digital',
            startRange: startDateRange,
            endRange: endDateRange,
            page: currentPage,
            pageSize,
            search: searchTerm,
        }));
    }, [company.id, startDateRange, endDateRange, currentPage, pageSize, searchTerm, isConfigReady]);

    return (
        <>
            <FilterBar />
            <Grid container spacing={2}>
                {/* Digital Physician Engagement */}
                <Grid item xs={12}>
                    <DigitalEngagementInfo />
                </Grid>
                {/* PhysicianEngagement by Channels and Segments */}
                <Grid item xs={12}>
                    <PhysicianEngagement />
                </Grid>
                {/* Successful Engagement Events  */}
                <Grid item xs={12}>
                    <SuccessfulEvents/>
                </Grid>
                {/* Physician Engagement by Deciles and Specialty*/}
                {
                    company.id === XEVINAPANT_COMPANY_ID ? (
                        <Grid item xs={12}>
                            <DecileSpecialty />
                        </Grid>) : null
                }
                {/* District Breakdown of Digital Physician Engagement */}     
                <Grid item xs={12}>
                    <DistrictBreakdown />
                </Grid>
                {/* Active site level Breakdown of Digital Physician Engagement */}    
                {hospitalBreakDownCompanies.includes(company.id) ?
                    <Grid item xs={12}>
                        <HospitalBreakdown/>
                    </Grid>: null
                }  
                {/* PowerBi */}
                <Grid item className='mt-3 mb-3' xs={12}>
                    <PowerBi/>
                </Grid>
                {/* Filters and Search */}
                <Grid item xs={12}>
                    <SearchUsingFilter />
                </Grid>
                {/* List of Npi */}
                <Grid item xs={12} sx={{ minWidth: 0 }}>
                    <NPITable />
                </Grid>
            </Grid>
        </>
    );
};

export default DigitalPhysicianEngagement;
